import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FadeUp, MaskLine, Overline } from "@/components/Reveal";

const FASCINATIONS = [
  "How they think.",
  "How they communicate.",
  "What makes them feel safe.",
  "What makes them defensive.",
  "Why brilliant people sometimes struggle.",
  "Why some teams click and others implode.",
  "Why customers stay.",
  "Why employees leave.",
  "Why people underestimate their own gifts.",
];

const About = () => (
  <>
    <section data-testid="about-hero" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
        <div>
          <Overline>About Jocelyn</Overline>
          <h1 className="mt-6 font-serif font-medium tracking-tight leading-[1.08] text-ink text-4xl sm:text-5xl lg:text-6xl">
            <MaskLine>I've basically made a career</MaskLine>
            <MaskLine delay={0.12}>out of being <em className="text-clay">fascinated</em></MaskLine>
            <MaskLine delay={0.24}>by people.</MaskLine>
          </h1>
          <div className="mt-10 space-y-1.5">
            {FASCINATIONS.map((line, i) => (
              <FadeUp key={line} delay={0.05 * i}>
                <p className="font-serif text-xl sm:text-2xl italic text-soot/80">{line}</p>
              </FadeUp>
            ))}
            <FadeUp delay={0.5}>
              <p className="pt-4 font-serif text-xl sm:text-2xl italic text-ink">
                And why changing the way someone sees themselves can sometimes change{" "}
                <span className="text-clay">everything.</span>
              </p>
            </FadeUp>
          </div>
        </div>
        <FadeUp delay={0.2}>
          <div className="relative mx-auto w-full max-w-md lg:max-w-none">
            <img
              src="/images/jocelyn-warm-smile.jpg"
              alt="Jocelyn MacDonald smiling warmly in an outdoor garden portrait"
              data-testid="about-portrait"
              className="w-full aspect-[4/5] object-cover rounded-2xl border border-soot/10 shadow-[0_40px_80px_-35px_rgba(22,21,19,0.4)]"
            />
            <p className="absolute -bottom-5 left-6 font-hand text-2xl text-ink bg-bone px-3 py-1 -rotate-2 shadow-sm">
              hi, I'm Jocelyn
            </p>
          </div>
        </FadeUp>
      </div>
    </section>

    <section data-testid="about-story" className="bg-cream py-24 sm:py-28 border-y border-soot/10">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <FadeUp>
          <Overline>The Story</Overline>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.12] text-ink">
            Coach. Consultant. Real-life <em className="text-clay">empire builder.</em>
          </h2>
        </FadeUp>
        <FadeUp delay={0.1} className="mt-10 space-y-6 text-base sm:text-lg leading-relaxed text-mute">
          <p>
            I've spent more than two decades in real organizations doing real work — sales, marketing,
            operations, customer experience, employee experience, leadership, business growth, consulting,
            and coaching. I've led teams, scaled small businesses, mentored leaders, and sat in the seat
            where the hard calls get made.
          </p>
          <p>
            Alongside all of that, I'm a formally trained ontological coach, with several years of formal
            training and one-on-one client work since 2019.
          </p>
          <p>
            If you've never heard of ontological coaching, here's the simple version: it explores not only
            what a person <em className="font-serif text-ink">does</em>, but the way they are{" "}
            <em className="font-serif text-ink">being</em> — the beliefs, language, emotions, assumptions, and
            patterns shaping how they interpret and respond to the world.
          </p>
          <p>
            In practice, that means we don't just work on your to-do list. We work on the operating system
            running underneath it — which is why the shifts tend to show up everywhere: in leadership, in
            communication, in relationships, in business results, and in how life feels day to day.
          </p>
          <p>
            I learned the theories so we can apply them, not so I can offer you platitudes. Whether we're
            tackling burnout, a business bottleneck, or a big life shift, you'll get a blend of tools,
            strategy, and grounded support — the kind of partnership that actually creates a difference.
          </p>
        </FadeUp>
        <FadeUp delay={0.15}>
          <blockquote
            data-testid="about-training-quote"
            className="mt-14 border-l-2 border-clay pl-6 sm:pl-8"
          >
            <p className="font-serif text-2xl sm:text-3xl italic font-medium leading-[1.25] text-ink">
              "Formally trained as an ontological coach. Informally trained by more than 20 years of leading
              people, growing businesses, navigating hard things, and paying very close attention."
            </p>
          </blockquote>
        </FadeUp>
      </div>
    </section>

    <section data-testid="about-personal" className="py-24 sm:py-28">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid gap-12 lg:grid-cols-2 lg:gap-20 lg:items-center">
        <FadeUp>
          <img
            src="/images/jocelyn-studio.jpg"
            alt="Jocelyn MacDonald seated at a table in a clean studio portrait, looking directly at the camera"
            data-testid="about-studio-portrait"
            className="w-full aspect-[4/5] sm:aspect-square object-cover rounded-2xl border border-soot/10 grayscale hover:grayscale-0 transition-all duration-700"
          />
        </FadeUp>
        <FadeUp delay={0.12}>
          <Overline>The Human Part</Overline>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl font-medium tracking-tight leading-[1.12] text-ink">
            I don't coach from <em className="text-clay">a pedestal.</em>
          </h2>
          <div className="mt-8 space-y-6 text-base sm:text-lg leading-relaxed text-mute">
            <p>
              I've lived a lot of life. I've left an emotionally abusive marriage, raised kids on my own,
              rebuilt from scratch, and led teams through chaos and growth.
            </p>
            <p>
              So no — I won't pretend I have all the answers. What I will do is help you find the answers
              that are right for you, with heart, humor, and the kind of honesty that moves you forward.
            </p>
            <p>
              From my experience, my training, and a genuine fascination with human beings, I can promise
              you this: the work won't always be easy, but it will be worth it. And we'll be on your path
              together.
            </p>
          </div>
          <Link
            to="/work-with-jocelyn"
            data-testid="about-cta-work-together"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-xs font-sans font-bold uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-clay"
          >
            Ways We Can Work Together <ArrowRight className="h-4 w-4" />
          </Link>
        </FadeUp>
      </div>
    </section>

    <section data-testid="about-cta" className="bg-ink text-[#FAF8F5] py-24 sm:py-28">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
        <FadeUp>
          <h2 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight leading-[1.1]">
            Want the résumé version, or the <em className="text-clay">real</em> version?
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#FAF8F5]/70 leading-relaxed">
            The Experience page has the metrics and milestones. The contact page has me.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link
              to="/experience"
              data-testid="about-cta-experience"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-xs font-sans font-bold uppercase tracking-[0.2em] text-[#FAF8F5] transition-colors duration-300 hover:bg-[#FAF8F5] hover:text-ink"
            >
              Explore My Experience
            </Link>
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="inline-flex items-center gap-2 rounded-full bg-clay px-7 py-3.5 text-xs font-sans font-bold uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-claydark"
            >
              Let's Talk <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </FadeUp>
      </div>
    </section>
  </>
);

export default About;
